/**
 * Breakpoint Hooks - A365 Core
 * 
 * Hooks personalizados para usar el servicio de breakpoints de forma reactiva.
 * Elimina la dependencia de Material-UI useTheme/useMediaQuery.
 * 
 * @author A365 Core Team
 * @version 1.0.0
 */

import { useState, useEffect } from 'react';
import { 
  getCurrentBreakpoint, 
  isMobile as isMobileUtil,
  isTablet as isTabletUtil,
  isDesktop as isDesktopUtil,
  isBreakpointDown,
  isBreakpointUp
} from '../services/breakpointService';

/**
 * Hook para obtener el breakpoint actual de forma reactiva
 * @returns {string} Breakpoint actual ('xs', 'sm', 'md', 'lg', 'xl', '2xl')
 */
export const useBreakpoint = () => {
  const [breakpoint, setBreakpoint] = useState(() => getCurrentBreakpoint());

  useEffect(() => {
    const handleResize = () => {
      setBreakpoint(getCurrentBreakpoint());
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return breakpoint;
};

/**
 * Hook para detectar si el dispositivo es móvil de forma reactiva
 * Equivalente a useMediaQuery(theme.breakpoints.down('xl'))
 * @returns {boolean}
 */
export const useIsMobile = () => {
  const breakpoint = useBreakpoint();
  return isMobileUtil(breakpoint);
};

/**
 * Hook para detectar si el dispositivo es tablet de forma reactiva
 * @returns {boolean}
 */
export const useIsTablet = () => {
  const breakpoint = useBreakpoint();
  return isTabletUtil(breakpoint);
};

/**
 * Hook para detectar si el dispositivo es desktop de forma reactiva
 * @returns {boolean}
 */
export const useIsDesktop = () => {
  const breakpoint = useBreakpoint();
  return isDesktopUtil(breakpoint);
};

/**
 * Hook para verificar si el breakpoint actual es menor o igual al especificado
 * @param {string} targetBreakpoint - Breakpoint objetivo
 * @returns {boolean}
 */
export const useBreakpointDown = (targetBreakpoint) => {
  const breakpoint = useBreakpoint();
  return isBreakpointDown(targetBreakpoint, breakpoint);
};

/**
 * Hook para verificar si el breakpoint actual es mayor o igual al especificado
 * @param {string} targetBreakpoint - Breakpoint objetivo
 * @returns {boolean}
 */
export const useBreakpointUp = (targetBreakpoint) => {
  const breakpoint = useBreakpoint();
  return isBreakpointUp(targetBreakpoint, breakpoint);
};
